import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { resetAuthState, useLogoutMutation } from '@/entities/Auth';
import { getMyProfile } from '@/entities/Profile';
import { Svg } from '@/shared/ui/Svg';
import ArrowIcon from '@/shared/assets/icons/complex/arrow.complex.svg';
import UserIcon from '@/shared/assets/icons/user.svg';
import SettingsIcon from '@/shared/assets/icons/sidebar/settings.svg';
import ExitIcon from '@/shared/assets/icons/sidebar/exit.svg';
import { routerPath } from '@/shared/config/router';
import styles from './UserProfile.module.scss';

export const UserProfile = () => {
  const user = useSelector(getMyProfile);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const navigate = useNavigate();
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();

  const logoutHandler = async (redirectPath?: string) => {
    if (isLoggingOut) return;

    // Серверный выход гасит сессию на всех устройствах, включая мобильное.
    // Упавший запрос (нет сети, токен уже протух) не должен запирать
    // пользователя в приложении — локальный выход делаем в любом случае.
    try {
      await logout().unwrap();
    } catch {
      // no-op
    }

    // Очищаем auth состояние и кеш RTK Query
    resetAuthState();

    if (redirectPath) {
      navigate(redirectPath);
    }
  };


  const initials = useMemo(() => {
    if (user?.avatar) return '';
    const firstName = user?.firstName || '';
    const lastName = user?.lastName || '';
    const username = user?.username || '';
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (username) {
      return username[0].toUpperCase();
    }
    return 'S';
  }, [user?.firstName, user?.lastName, user?.username, user?.avatar]);

  const avatarSrc = user?.avatar || null;

  return (
    <div className={styles.root} ref={menuRef}>
      {user && (
        <>
          <button
            type="button"
            className={styles.trigger}
            onClick={handleMenuToggle}
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-neutral-200 overflow-hidden flex items-center justify-center">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={`${user.firstName} ${user.lastName}`.trim() || 'User'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm font-medium text-grey-secondary">{initials}</span>
              )}
            </div>

            <div className="flex flex-col">
              <span className={styles.name}>
                {user?.firstName} {user?.lastName}
              </span>
              <span className="text-sm text-grey-secondary">{user?.email || ''}</span>
            </div>
            <span className={styles.button}>
              <Svg Icon={ArrowIcon} className={isMenuOpen ? styles.arrowRotated : ''} />
            </span>
          </button>

          {isMenuOpen && (
            <div className={styles.dropdown}>
              <>
                <Link className={styles.menuItem} to={routerPath.profile}>
                  <Svg Icon={UserIcon} />
                  <span>My profile</span>
                </Link>
                <div className={styles.divider} />
              </>
              <Link className={styles.menuItem} to="/settings">
                <Svg Icon={SettingsIcon} />
                <span>Settings</span>
              </Link>
              <div className={styles.divider} />
              <div
                role="presentation"
                className={`${styles.menuItem} ${styles.logoutItem}`}
                onClick={() => logoutHandler(routerPath.login)}
              >
                <Svg Icon={ExitIcon} />
                <span>Log out</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
